import { useEffect, useRef, useState } from "react";
import { Search, Phone, Clock, Calendar, FileText, Mic } from "lucide-react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { CallAudioPlayer } from "./CallAudioPlayer";
import { SERA_PHONE } from "@/components/atomic-crm/misc/constants";

const supabase = getSupabaseClient();

interface Conversation {
  id: string;
  patientName: string;
  phoneNumber: string;
  timestamp: string;
  duration: string;
  durationSeconds: number;
  outcome: "Booked" | "Info" | "Missed";
  aiSummary: string;
  transcript: string | null;
  appointmentDate: string;
  appointmentTime: string;
  clinicNotes: string;
  audioUrl: string | null;
  pauseTimestamps: number[];
}

const mapRow = (row: Record<string, unknown>): Conversation => {
  const ds = row.duration_seconds as number | null;
  return {
    id: String(row.id),
    patientName: (row.caller_name as string | null) || "Unknown",
    phoneNumber: (row.phone_number as string | null) || "",
    timestamp: (row.created_at as string) || new Date().toISOString(),
    duration: ds ? `${Math.floor(ds / 60)}m ${ds % 60}s` : "—",
    durationSeconds: ds ?? 0,
    outcome: (row.appointment_booked as boolean)
      ? "Booked"
      : (row.outcome as string) === "missed"
        ? "Missed"
        : "Info",
    aiSummary: (row.summary as string | null) || "",
    transcript: (row.transcript as string | null) ?? null,
    appointmentDate: (row.appointment_date as string | null) || "",
    appointmentTime: (row.appointment_time as string | null) || "",
    clinicNotes: (row.clinic_notes as string | null) || "",
    audioUrl: (row.audio_url as string | null) ?? null,
    pauseTimestamps: Array.isArray(row.pause_timestamps)
      ? (row.pause_timestamps as number[])
      : [],
  };
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const OUTCOME_CONFIG = {
  Booked: {
    dot: "#22C55E",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    text: "#16a34a",
    label: "Booked",
  },
  Info: {
    dot: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
    text: "#92400E",
    label: "Info",
  },
  Missed: {
    dot: "#EF4444",
    bg: "#FEF2F2",
    border: "#FECACA",
    text: "#dc2626",
    label: "Missed",
  },
};

const CallerAvatar = ({ name, size = 36 }: { name: string; size?: number }) => {
  const letter = (name || "U")[0].toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#F7F7F7",
        border: "1px solid #E8E8E8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: size * 0.38,
        fontWeight: 600,
        color: "#555555",
        letterSpacing: "-0.01em",
      }}
    >
      {letter}
    </div>
  );
};

const OutcomeBadge = ({
  outcome,
}: {
  outcome: "Booked" | "Info" | "Missed";
}) => {
  const cfg = OUTCOME_CONFIG[outcome];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 100,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.text,
        letterSpacing: "0.02em",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </div>
  );
};

const TranscriptView = ({ raw }: { raw: string | null }) => {
  if (!raw) return null;
  // Ensure we always work with a string (Supabase JSONB columns can return objects)
  const rawStr = typeof raw === "string" ? raw : JSON.stringify(raw);
  let turns: { role: string; text: string }[] = [];
  try {
    const parsed = JSON.parse(rawStr);
    if (Array.isArray(parsed)) {
      turns = parsed.map((t: Record<string, string>) => ({
        role: t.role ?? t.speaker ?? "agent",
        text: t.text ?? t.content ?? t.message ?? "",
      }));
    }
  } catch {
    turns = rawStr
      .split(/\n+/)
      .filter(Boolean)
      .map((line) => ({ role: "log", text: line }));
  }
  if (turns.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {turns.map((t, i) => {
        const isAgent =
          t.role === "agent" || t.role === "assistant" || t.role === "sera";
        return (
          <div
            key={i}
            style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: isAgent ? "#DCFCE7" : "#F3F4F6",
                border: `1px solid ${isAgent ? "#BBF7D0" : "#E5E7EB"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 1,
                fontSize: 8,
                fontWeight: 700,
                color: isAgent ? "#16A34A" : "#6B7280",
                letterSpacing: "0.02em",
              }}
            >
              {isAgent ? "S" : "C"}
            </div>
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: isAgent ? "#16A34A" : "#9CA3AF",
                  marginBottom: 3,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {isAgent ? "Sera" : t.role === "log" ? "" : "Caller"}
              </p>
              <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65 }}>
                {t.text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── LEFT SIDEBAR ────────────────────────────────────────────────────────────

const LeftSidebar = ({
  filter,
  setFilter,
  counts,
  live,
  totalCalls,
}: {
  filter: string;
  setFilter: (f: "All" | "Booked" | "Missed" | "Info") => void;
  counts: Record<string, number>;
  live: boolean;
  totalCalls: number;
}) => {
  const filters: {
    label: "All" | "Booked" | "Missed" | "Info";
    icon: string;
    color: string;
  }[] = [
    { label: "All", icon: "≡", color: "#6B7280" },
    { label: "Booked", icon: "✓", color: "#16A34A" },
    { label: "Missed", icon: "✕", color: "#DC2626" },
    { label: "Info", icon: "i", color: "#D97706" },
  ];

  const bookingRate =
    totalCalls > 0 ? Math.round((counts.Booked / totalCalls) * 100) : 0;

  return (
    <div
      style={{
        width: 220,
        flexShrink: 0,
        borderRight: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* Title */}
      <div style={{ padding: "20px 16px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.02em",
            }}
          >
            Conversations
          </p>
          {live && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#16A34A",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          )}
        </div>
        <p style={{ fontSize: 12, color: "#9CA3AF" }}>
          {totalCalls} call{totalCalls !== 1 ? "s" : ""} total
        </p>
      </div>

      {/* Filters */}
      <div style={{ padding: "0 10px", flex: 1, overflowY: "auto" }}>
        <p
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#9CA3AF",
            marginBottom: 6,
            paddingLeft: 8,
          }}
        >
          Filter
        </p>
        {filters.map((f) => {
          const active = filter === f.label;
          return (
            <button
              key={f.label}
              onClick={() => setFilter(f.label)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "7px 10px",
                borderRadius: 7,
                background: active ? "#F7F7F7" : "transparent",
                border: "1px solid transparent",
                cursor: "pointer",
                marginBottom: 2,
                transition: "all 0.1s",
              }}
              onMouseEnter={(e) => {
                if (!active)
                  (e.currentTarget as HTMLElement).style.background = "#F7F7F7";
              }}
              onMouseLeave={(e) => {
                if (!active)
                  (e.currentTarget as HTMLElement).style.background =
                    "transparent";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    background: "#F7F7F7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: active ? "#111111" : "#999999",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: active ? 600 : 400,
                    color: active ? "#111111" : "#555555",
                  }}
                >
                  {f.label}
                </span>
              </div>
              {counts[f.label] > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? "#111111" : "#999999",
                    background: active ? "#E8E8E8" : "#F7F7F7",
                    borderRadius: 100,
                    padding: "1px 7px",
                  }}
                >
                  {counts[f.label]}
                </span>
              )}
            </button>
          );
        })}

        {/* Mini stats */}
        <div style={{ marginTop: 20 }}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "#9CA3AF",
              marginBottom: 8,
              paddingLeft: 8,
            }}
          >
            Stats
          </p>
          <div
            style={{
              background: "#F7F7F7",
              border: "1px solid #E8E8E8",
              borderRadius: 10,
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div>
              <p style={{ fontSize: 10, color: "#999999", marginBottom: 2 }}>
                Booking rate
              </p>
              <p
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#111111",
                  letterSpacing: "-0.02em",
                }}
              >
                {bookingRate}%
              </p>
            </div>
            <div style={{ height: 1, background: "#E8E8E8" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <p style={{ fontSize: 10, color: "#999999", marginBottom: 1 }}>
                  Booked
                </p>
                <p style={{ fontSize: 15, fontWeight: 700, color: "#22C55E" }}>
                  {counts.Booked}
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 1 }}>
                  Missed
                </p>
                <p style={{ fontSize: 15, fontWeight: 700, color: "#DC2626" }}>
                  {counts.Missed}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo line */}
      <div style={{ padding: "12px 16px", borderTop: "1px solid #F3F4F6" }}>
        <p style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 2 }}>
          Demo line
        </p>
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            fontFamily: "monospace",
          }}
        >
          {SERA_PHONE}
        </p>
      </div>
    </div>
  );
};

// ─── CENTER CALL LIST ────────────────────────────────────────────────────────

const CallListItem = ({
  conv,
  selected,
  onClick,
}: {
  conv: Conversation;
  selected: boolean;
  onClick: () => void;
}) => {
  const cfg = OUTCOME_CONFIG[conv.outcome];
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 11,
        padding: "12px 14px",
        background: selected ? "#F7F7F7" : "#FFFFFF",
        border: "none",
        borderLeft: selected ? "2px solid #111111" : "2px solid transparent",
        borderBottom: "1px solid #E8E8E8",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#F7F7F7";
      }}
      onMouseLeave={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
      }}
    >
      <div style={{ position: "relative", marginTop: 1 }}>
        <CallerAvatar name={conv.patientName} size={34} />
        <span
          style={{
            position: "absolute",
            bottom: -1,
            right: -1,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: cfg.dot,
            border: "1.5px solid #FFFFFF",
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 3,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#111827",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {conv.patientName}
          </p>
          <span
            style={{
              fontSize: 11,
              color: "#9CA3AF",
              flexShrink: 0,
              marginLeft: 6,
            }}
          >
            {timeAgo(conv.timestamp)}
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: "#6B7280",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.5,
            marginBottom: 4,
          }}
        >
          {conv.aiSummary || conv.phoneNumber || "No summary"}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 11, color: "#9CA3AF" }}>
            {conv.duration}
          </span>
          {conv.audioUrl && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "1px 6px",
                borderRadius: 4,
                background: "#DBEAFE",
                color: "#2563EB",
                letterSpacing: "0.03em",
              }}
            >
              REC
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// ─── RIGHT DETAIL PANEL ─────────────────────────────────────────────────────

const DetailPanel = ({
  conv,
  note,
  onNoteChange,
  onSaveNote,
  saving,
}: {
  conv: Conversation;
  note: string;
  onNoteChange: (v: string) => void;
  onSaveNote: () => void;
  saving: boolean;
}) => {
  const [tab, setTab] = useState<"summary" | "transcript" | "audio" | "notes">(
    "summary",
  );
  const cfg = OUTCOME_CONFIG[conv.outcome];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 22px 14px",
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 13,
            marginBottom: 12,
          }}
        >
          <CallerAvatar name={conv.patientName} size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#111827",
                letterSpacing: "-0.02em",
                marginBottom: 2,
              }}
            >
              {conv.patientName}
            </p>
            <p
              style={{
                fontSize: 12,
                color: "#9CA3AF",
                fontFamily: "monospace",
              }}
            >
              {conv.phoneNumber || "Unknown number"}
            </p>
          </div>
          <OutcomeBadge outcome={conv.outcome} />
        </div>

        {/* Meta row */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {[
            { icon: <Clock size={11} />, label: conv.duration },
            { icon: <Phone size={11} />, label: timeAgo(conv.timestamp) },
            ...(conv.appointmentDate
              ? [
                  {
                    icon: <Calendar size={11} />,
                    label: `${conv.appointmentDate}${conv.appointmentTime ? ` at ${conv.appointmentTime}` : ""}`,
                  },
                ]
              : []),
          ].map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: "#9CA3AF",
                fontSize: 12,
              }}
            >
              <span style={{ color: "#D1D5DB" }}>{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* AI Summary Card */}
      {conv.aiSummary && (
        <div
          style={{
            margin: "14px 18px 0",
            padding: "12px 14px",
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 7,
            }}
          >
            <div
              style={{
                width: 15,
                height: 15,
                borderRadius: 4,
                background: "#DCFCE7",
                border: "1px solid #BBF7D0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 8, color: "#16A34A" }}>✦</span>
            </div>
            <p
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#16A34A",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
              }}
            >
              AI Summary
            </p>
          </div>
          <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65 }}>
            {conv.aiSummary}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "1px solid #E5E7EB",
          padding: "0 18px",
          marginTop: 14,
          flexShrink: 0,
        }}
      >
        {[
          {
            id: "summary" as const,
            label: "Details",
            icon: <FileText size={12} />,
          },
          {
            id: "transcript" as const,
            label: "Transcript",
            icon: <Phone size={12} />,
            hide: !conv.transcript,
          },
          {
            id: "audio" as const,
            label: "Recording",
            icon: <Mic size={12} />,
            hide: !conv.audioUrl,
          },
          {
            id: "notes" as const,
            label: "Notes",
            icon: <FileText size={12} />,
          },
        ]
          .filter((t) => !t.hide)
          .map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: tab === t.id ? 600 : 400,
                color: tab === t.id ? "#111827" : "#6B7280",
                background: "transparent",
                border: "none",
                borderBottom:
                  tab === t.id ? "2px solid #111111" : "2px solid transparent",
                padding: "8px 10px",
                cursor: "pointer",
                transition: "color 0.1s",
                marginBottom: -1,
              }}
            >
              <span style={{ color: tab === t.id ? "#16A34A" : "#D1D5DB" }}>
                {t.icon}
              </span>
              {t.label}
            </button>
          ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px" }}>
        {tab === "summary" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {conv.outcome === "Booked" && conv.appointmentDate && (
              <div
                style={{
                  padding: "12px 14px",
                  background: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: 9,
                }}
              >
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#16A34A",
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    marginBottom: 5,
                  }}
                >
                  Appointment booked
                </p>
                <p style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  {conv.appointmentDate}
                  {conv.appointmentTime && ` · ${conv.appointmentTime}`}
                </p>
              </div>
            )}

            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: 9,
                overflow: "hidden",
              }}
            >
              {[
                { label: "Phone", value: conv.phoneNumber || "—" },
                { label: "Duration", value: conv.duration },
                {
                  label: "Called",
                  value: new Date(conv.timestamp).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  }),
                },
                { label: "Outcome", value: conv.outcome },
              ].map((row, i, arr) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "9px 13px",
                    borderBottom:
                      i < arr.length - 1 ? "1px solid #F3F4F6" : "none",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#9CA3AF" }}>
                    {row.label}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: row.label === "Outcome" ? cfg.text : "#374151",
                      fontFamily:
                        row.label === "Phone" ? "monospace" : "inherit",
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "transcript" && (
          <div>
            {conv.transcript ? (
              <TranscriptView raw={conv.transcript} />
            ) : (
              <p
                style={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  textAlign: "center",
                  paddingTop: 32,
                }}
              >
                No transcript available
              </p>
            )}
          </div>
        )}

        {tab === "audio" && (
          <div>
            {conv.audioUrl ? (
              <CallAudioPlayer
                audioUrl={conv.audioUrl}
                pauseTimestamps={conv.pauseTimestamps}
                durationSeconds={conv.durationSeconds}
              />
            ) : (
              <p
                style={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  textAlign: "center",
                  paddingTop: 32,
                }}
              >
                No recording available
              </p>
            )}
          </div>
        )}

        {tab === "notes" && (
          <div>
            <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>
              Internal notes visible only to clinic staff.
            </p>
            <textarea
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Add notes about this caller or appointment…"
              rows={6}
              style={{
                width: "100%",
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                padding: "10px 12px",
                background: "#FFFFFF",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                color: "#111827",
                lineHeight: 1.65,
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#111111";
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#E8E8E8";
              }}
            />
            <button
              onClick={onSaveNote}
              disabled={saving}
              style={{
                marginTop: 10,
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 18px",
                borderRadius: 7,
                background: saving ? "#555555" : "#111111",
                color: "#FFFFFF",
                border: "none",
                cursor: saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save notes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export const ConversationsPage = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"All" | "Booked" | "Missed" | "Info">(
    "All",
  );
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .order("created_at", { ascending: false });
      if (!error && data) {
        const mapped = data.map(mapRow);
        setConversations(mapped);
        const n: Record<string, string> = {};
        mapped.forEach((c) => {
          n[c.id] = c.clinicNotes;
        });
        setNotes(n);
        if (mapped.length > 0) setSelectedId(mapped[0].id);
      }
      setLoading(false);
    };
    fetchData();

    const ch = supabase
      .channel("conversations-live")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "conversations" },
        (payload) => {
          const row = mapRow(payload.new as Record<string, unknown>);
          setConversations((prev) => [row, ...prev]);
          setNotes((prev) => ({ ...prev, [row.id]: row.clinicNotes }));
        },
      )
      .subscribe((status) => {
        setLive(status === "SUBSCRIBED");
      });

    channelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const saveNote = async (id: string) => {
    setSavingId(id);
    try {
      await supabase
        .from("conversations")
        .update({ clinic_notes: notes[id] ?? "" })
        .eq("id", id);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, clinicNotes: notes[id] ?? "" } : c,
        ),
      );
    } finally {
      setSavingId(null);
    }
  };

  const visible = conversations.filter((c) => {
    const matchFilter = filter === "All" || c.outcome === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      c.patientName.toLowerCase().includes(q) ||
      c.phoneNumber.includes(q);
    return matchFilter && matchSearch;
  });

  const counts = {
    All: conversations.length,
    Booked: conversations.filter((c) => c.outcome === "Booked").length,
    Missed: conversations.filter((c) => c.outcome === "Missed").length,
    Info: conversations.filter((c) => c.outcome === "Info").length,
  };

  const selectedConv = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 56px)",
        background: "#F7F7F7",
        overflow: "hidden",
      }}
    >
      {/* Column 1: Left sidebar */}
      <LeftSidebar
        filter={filter}
        setFilter={setFilter}
        counts={counts}
        live={live}
        totalCalls={conversations.length}
      />

      {/* Column 2: Call list */}
      <div
        style={{
          width: 320,
          flexShrink: 0,
          borderRight: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: "#FFFFFF",
        }}
      >
        {/* Search */}
        <div
          style={{
            padding: "12px 12px 10px",
            borderBottom: "1px solid #F3F4F6",
            flexShrink: 0,
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 13,
                height: 13,
                color: "#9CA3AF",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search calls…"
              style={{
                width: "100%",
                fontSize: 13,
                background: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: 7,
                paddingLeft: 30,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                color: "#111827",
                outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#16A34A";
                (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "#E5E7EB";
                (e.currentTarget as HTMLElement).style.background = "#F9FAFB";
              }}
            />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div
              style={{
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 68,
                    borderRadius: 8,
                    background: "#F3F4F6",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: 56,
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 4,
                }}
              >
                <Phone size={16} color="#9CA3AF" />
              </div>
              <p style={{ fontSize: 13, fontWeight: 600, color: "#6B7280" }}>
                {search ? "No results" : "No calls yet"}
              </p>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>{SERA_PHONE}</p>
            </div>
          ) : (
            visible.map((conv) => (
              <CallListItem
                key={conv.id}
                conv={conv}
                selected={selectedId === conv.id}
                onClick={() => setSelectedId(conv.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Column 3: Detail panel */}
      <div style={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
        {selectedConv ? (
          <DetailPanel
            conv={selectedConv}
            note={notes[selectedConv.id] ?? ""}
            onNoteChange={(v) =>
              setNotes((prev) => ({ ...prev, [selectedConv.id]: v }))
            }
            onSaveNote={() => saveNote(selectedConv.id)}
            saving={savingId === selectedConv.id}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 10,
              background: "#FFFFFF",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#F3F4F6",
                border: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Phone size={20} color="#D1D5DB" />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
              Select a conversation
            </p>
            <p style={{ fontSize: 12, color: "#9CA3AF" }}>
              Click any call to view details
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
};
